import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, AlertCircle } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";

interface InvitationData {
  id: string;
  email: string;
  status: string;
  expires_at: string;
  organization_id: string;
  organization?: {
    name: string;
  };
}

// Service function for fetching invitation (follows TanStack Query pattern)
async function fetchInvitation(token: string): Promise<InvitationData> {
  const { data, error } = await supabase
    .from("team_invitations")
    .select("*, organization:organizations(name)")
    .eq("token", token)
    .single();

  if (error || !data) {
    throw new Error("Invalid or removed invitation");
  }

  return data;
}

// Service function for getting current user
async function getCurrentUser() {
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();

  if (error) throw error;
  return user;
}

export default function AcceptInvite() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [message, setMessage] = useState<{
    type: "info" | "error";
    text: string;
  } | null>(null);

  const token = searchParams.get("token");
  const orgId = searchParams.get("org");

  // TanStack Query: Fetch invitation data (2025 best practice)
  const {
    data: invitation,
    isLoading,
    error: invitationError,
  } = useQuery({
    queryKey: ["invitation", token],
    queryFn: () => fetchInvitation(token!),
    enabled: !!token,
    retry: false,
    staleTime: 0, // Always fetch fresh invitation data
  });

  // Redirect handler (memoized to avoid recreating on every render)
  const handleRedirect = useCallback(
    (path: string, delay: number = 2000) => {
      setTimeout(() => navigate(path), delay);
    },
    [navigate],
  );

  // Handle invitation validation and auth flow (side effects - legitimate useEffect usage)
  useEffect(() => {
    // Don't run until we have query results
    if (isLoading) return;

    const processInvitation = async () => {
      // No token provided
      if (!token) {
        setMessage({
          type: "error",
          text: "Invalid invitation link. The invitation token is missing.",
        });
        handleRedirect("/login", 3000);
        return;
      }

      // Invitation fetch failed
      if (invitationError || !invitation) {
        setMessage({
          type: "error",
          text: "This invitation link is invalid or has been removed.",
        });
        handleRedirect("/login", 3000);
        return;
      }

      // Check if invitation is expired
      if (
        invitation.status === "expired" ||
        new Date() > new Date(invitation.expires_at)
      ) {
        setMessage({
          type: "error",
          text: "This invitation has expired. Please request a new invitation.",
        });
        handleRedirect("/login", 3000);
        return;
      }

      // Check if invitation was already accepted
      if (invitation.status === "accepted") {
        setMessage({
          type: "info",
          text: "This invitation has already been accepted.",
        });
        handleRedirect("/", 2000);
        return;
      }

      // Check authentication
      try {
        const user = await getCurrentUser();

        if (!user) {
          // User needs to sign in first
          const redirectUrl = `/accept-invite?token=${token}${orgId ? `&org=${orgId}` : ""}`;
          sessionStorage.setItem("authRedirect", redirectUrl);
          sessionStorage.setItem("invitationEmail", invitation.email);

          setMessage({
            type: "info",
            text: `You've been invited to join ${invitation.organization?.name || "a team"}! Please sign in to continue.`,
          });
          handleRedirect(`/login?redirect=${encodeURIComponent(redirectUrl)}`);
          return;
        }

        // SECURITY CHECK: Verify that the logged-in user matches the invitation email
        if (user.email?.toLowerCase() !== invitation.email?.toLowerCase()) {
          setMessage({
            type: "error",
            text: `This invitation is for ${invitation.email}. You are currently logged in as ${user.email}. Logging you out to continue...`,
          });

          // Store invitation details for after logout
          const redirectUrl = `/accept-invite?token=${token}${orgId ? `&org=${orgId}` : ""}`;
          sessionStorage.setItem("authRedirect", redirectUrl);
          sessionStorage.setItem("invitationEmail", invitation.email);

          // Log out the current user and redirect to login
          setTimeout(async () => {
            await supabase.auth.signOut();
            navigate(`/login?redirect=${encodeURIComponent(redirectUrl)}`);
          }, 3000);
          return;
        }

        // User is authenticated and email matches, redirect to invitation acceptance page
        // Clear sessionStorage since we've verified the correct user is logged in
        sessionStorage.removeItem("invitationEmail");
        sessionStorage.removeItem("authRedirect");
        navigate(`/invite/${token}${orgId ? `?org=${orgId}` : ""}`);
      } catch (error) {
        console.error("Error checking authentication:", error);
        setMessage({
          type: "error",
          text: "An error occurred while verifying your identity. Please try again.",
        });
        handleRedirect("/", 3000);
      }
    };

    processInvitation();
  }, [
    token,
    orgId,
    invitation,
    invitationError,
    isLoading,
    navigate,
    handleRedirect,
  ]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Card className="w-full max-w-md">
        {isLoading && !message ? (
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
              <p className="text-muted-foreground">
                Verifying your invitation...
              </p>
            </div>
          </CardContent>
        ) : message ? (
          <>
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                <AlertCircle
                  className={`h-6 w-6 ${message.type === "error" ? "text-destructive" : "text-primary"}`}
                />
              </div>
              <CardTitle className="text-xl">
                {message.type === "error" ? "Invitation Issue" : "Welcome!"}
              </CardTitle>
              <CardDescription className="mt-2">{message.text}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex justify-center">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">
                  Redirecting...
                </span>
              </div>
            </CardContent>
          </>
        ) : null}
      </Card>
    </div>
  );
}
